import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Loader } from "lucide-react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify } from "../../../helpers";
import { fetchRoles, getRolePermissionData, updateUserRolePermissions } from "../../../services/users";
import Grid from "../../Grid";
import Skeleton from "react-loading-skeleton";

export default function RoleSettings({ selectedPracticeChange, fetchChangePracticeData }) {
  let portal_account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    portal_account_id = selectedPracticeChange?.account_id;
  }

  const [reloadData, setReloadData] = useState(true);
  const [enableLoader, setEnableLoader] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [featureConfigsData, setFeatureConfigsData] = useState([]);
  const [checkAll, setCheckAll] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const isFetched = useRef(false);

  useEffect(() => {
    if (!isFetched.current) {
      loadAllRoles();
      fetchFeatureConfigData();
      isFetched.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllRoles = async () => {
    try {
      const response = await fetchRoles();
      if (response?.data) {
        getAllRoleData(response?.data);
      } else {
        setRolesData([]);
      }
    } catch (error) {
      console.error("Error fetching loadAllRoles data:", error);
    }
  };

  const getAllRoleData = (allRoles) => {
    try {
      const roleData = allRoles?.filter((role) => !["System"].includes(role?.role_name))?.map((role) => ({
        value: role?.role_name,
        label: role?.role_name,
      }));
      setRolesData(roleData);
      setReloadData(false);
    } catch (error) {
      console.error("Error to get role data:", error);
    }
  };

  const fetchFeatureConfigData = async (roleName) => {
    setEnableLoader(true);
    try {
      const response = await getRolePermissionData(roleName);
      if (response?.data) {
        const sortedResponse = response?.data.sort((a, b) => a.name.localeCompare(b.name));
        const fetchedData = sortedResponse.map((item) => ({
          ...item,
          value: checkAll ? true : roleName ? !!item?.value : false,
        }));
        setFeatureConfigsData(fetchedData);
      } else {
        setFeatureConfigsData([]);
      }
    } catch (error) {
      console.error("Error fetching feature config data:", error);
      setFeatureConfigsData([]);
    } finally {
      setEnableLoader(false);
    }
  };

  const SplitGridFeaturs = (sortedResponse, parts) => {
    const chunkSize = Math.ceil(sortedResponse?.length / parts);
    return Array.from({ length: parts }, (_, i) =>
      sortedResponse?.slice(i * chunkSize, i * chunkSize + chunkSize)
    );
  };

  const [firstColumn, secondColumn, thirdColumn, fourthColumn] = SplitGridFeaturs(featureConfigsData, 4);

  const handleSelectChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedRole(null);
      setFeatureConfigsData((prevData) =>
        prevData.map((item) => ({
          ...item,
          value: checkAll ? true : false,
        }))
      );
      return;
    }

    setSelectedRole(selectedOption.value);
    await fetchFeatureConfigData(selectedOption.value);
  };

  const handleCheckAll = (isChecked) => {
    setCheckAll(isChecked);
    setFeatureConfigsData((prevData) =>
      prevData.map((item) => ({
        ...item,
        value: isChecked,
      }))
    );
  };

  const handleSwitchChange = (id, checked) => {
    setFeatureConfigsData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, value: checked } : item
      )
    );
  };

  const handleFeatureConfigUpdate = async (e) => {
    e.preventDefault();

    if(can_visit("administrator") && can_visit("administrator_roles")) {
      try {
        setEnableLoader(true);
        e.target.disabled = true;

        const updatedPermissionData = featureConfigsData.map(({ id, value }) => ({ id, value }));
        const updatedData = {
          account_id: portal_account_id,
          role_name: selectedRole,
          permissions: updatedPermissionData,
        };

        const response = await updateUserRolePermissions(updatedData);

        if (response?.detail) {
          notify("error", response?.detail);
        } else if (response?.data?.status === "success") {
          notify("success", response?.data?.message);
          // setSelectedAccountIds([]);
          // setReloadData(true);
        } else {
          notify("error", response?.data?.message);
        }
      } catch (error) {
        console.error("Error updating permissions:", error);
        if (error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail);
        }
      } finally {
        setEnableLoader(false);
        e.target.disabled = false;
      }
    }
  };

  const FeatureList = ({ items }) => (
    items?.map((item, index) => (
      item?.name?.toLowerCase().includes('_role_') ? null : (
        <div key={index} className="docpro_checkbox">
          <input
            type="checkbox"
            name="global_schedule"
            value={item?.value}
            checked={item?.value}
            onChange={(e) => handleSwitchChange(item?.id, e.target.checked)}
          />
          <label style={{ marginLeft: "6px" }} className={`${item?.system_value !== true && 'docpro_red_color'} ${item?.name?.toLowerCase().includes('role') && 'docpro_crimson_color'} ${item?.name?.toLowerCase().includes('administrator') && 'docpro_indigo_color'}`}>{item?.name}</label>
        </div>
      )
    ))
  );

  useDocumentTitle("Role Permissions");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">Role Permissions</h2>
      </div>
      <Spacing lg="5" sm="10" />

      <div className="docpro_card">
        {reloadData ? (
          <>
            <Skeleton width="10rem" borderRadius="8px"></Skeleton>
            <Skeleton width="100%" height="3rem" borderRadius="8px"></Skeleton>
            <Spacing lg="15" sm="20" />
            <Skeleton width="10rem" borderRadius="8px"></Skeleton>
            <Spacing lg="15" sm="20" />
            <Grid variant="docpro_grid_gap_10 docpro_grid_col_4">
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["6rem","7rem","6rem","5rem","6rem","8rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["7.5rem","7rem","6rem","4rem","6rem","8rem","9rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["5rem","7rem","8rem","5rem","6rem","4rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["8rem","7rem","9rem","5rem","5rem","8rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
            </Grid>
          </>
        ) : (
          <form onSubmit={handleFeatureConfigUpdate} autoComplete="off" className="relative">
            <label className="docpro_fw_500 docpro_primary_color">
              Select Role <span className="docpro_red_color">*</span>
            </label>
            <Select
              required
              name="account_id"
              options={rolesData}
              placeholder="Search..."
              isSearchable
              value={rolesData.find((option) => option?.value === selectedRole) || null}
              onChange={handleSelectChange}
              className="react-select-container w-full"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: 'var(--border)',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 1050,
                  marginTop: '-2px',
                  borderRadius: '0 0 3px 3px',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 1050,
                }),
              }}
            />
            <Spacing lg="15" sm="25" />

            <div className="flex items-center justify-between docpro_grid_gap_20">
              <h2 className="docpro_fs_16 docpro_mb_0">Set Feature Permissions</h2>
              <div className="text-right" style={{ minWidth: "125px" }}>
                <div className="docpro_checkbox">
                  <input
                    type="checkbox"
                    name="check_all_yes"
                    checked={checkAll === true}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                  />
                  <label style={{ marginLeft: "8px" }}>Check All</label>
                </div>
              </div>
            </div>
            <Spacing lg="15" sm="25" />

            {featureConfigsData.length > 0 ? (
              <>
                <Grid variant="docpro_grid_gap_10 docpro_grid_col_4">
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={firstColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={secondColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={thirdColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={fourthColumn} />
                  </Grid>
                </Grid>
              </>
            ) : (
              <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }} >
                No data available
              </div>
            )}

            <Spacing lg="32" sm="32" />
            <div className="docpro_modal_btns">
              <button
                type="submit"
                className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: enableLoader ? "none" : "initial" }}
                disabled={enableLoader}
              >
                Save
                {enableLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </div>

            {enableLoader && (
              <div className="absolute top-0 left-0 w-full" style={{ height: "100%", zIndex: 3 }} >
                <div className="flex items-center loading" style={{ width: "100%", height: "100%", justifyContent: "center", background: "rgb(255 255 255 / 63%)" }} >
                  <span className="docpro_loading" style={{ position: "inherit" }}>
                    <Loader width={46} height={46} style={{ color: "var(--accent)" }} />
                  </span>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </>
  );
}
